/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import React, { useRef, useState } from "react"
import { Link, useHistory } from "react-router-dom"
import { Button, Grid, Icon, Placeholder } from "@cd2/components"
import { useTheme } from "@cd2/theme"
import { H3, P } from "@cd2/text"
import { siteColors } from "../../providers/ThemeProvider"
import { icons } from "../../utils/icons"
import { messageFromError } from "../../utils/error"
import { IComponentProp } from "@cd2/tools"
import { ErrorBoundaryRaw } from "../../ErrorBoundary"
import { QuickToolTip } from "../QuickTooltip"
import { useAsyncEffect } from "../../hooks/useAsyncEffect"
import { useLazyMemo } from "../../hooks/useLazyMemo"
import { LoadingFrame } from "../../pages/user/UserResultsShowPage/AssessmentDetailsTab"
import { Popover } from "../Popover"

export interface IHoverResourceLoaderProps<Data> {
  reloadKey?: any
  loadData(): Promise<Data>
  Target(props: { data: Data; isTitle?: boolean }): JSX.Element
  HoverContent(props: { data: Data }): JSX.Element
  viewLink?(data: Data): string | null
  openInNewTab?: boolean
  inline?: boolean
  resourceName?: string
  header?: React.ReactNode
  placement?: IComponentProp<typeof Popover, "placement">
  offset?: IComponentProp<typeof Popover, "offset">
  appendToBody?: boolean
  inlineViewLink?: boolean
  noHoverIcon?: boolean
}

export function HoverResourceLoader<Data>(props: IHoverResourceLoaderProps<Data>) {
  return (
    <ErrorBoundaryRaw
      renderOnError={error => (
        <QuickToolTip content={messageFromError(error)}>
          <div
            css={css`
              background: ${siteColors.errorRed};
              color: ${siteColors.errorRedText};
              padding: 4px;
              border-radius: 4px;
            `}
          >
            An error occurred loading this {props.resourceName?.toLowerCase() ?? "resource"}.
          </div>
        </QuickToolTip>
      )}
    >
      <HoverResourceLoaderInner {...props} />
    </ErrorBoundaryRaw>
  )
}
function HoverResourceLoaderInner<Data>({
  reloadKey,
  loadData,
  Target,
  HoverContent,
  viewLink,
  openInNewTab,
  resourceName,
  header,
  placement = "bottom",
  offset,
  inlineViewLink,
  noHoverIcon,
}: IHoverResourceLoaderProps<Data>) {
  const theme = useTheme()
  const history = useHistory()

  const [data, setData] = useState<Data | null>(null)
  const dataLoadingRef = useRef<null | number>(null)

  useAsyncEffect(async () => {
    dataLoadingRef.current = null

    if (data) {
      setData(null)
    }
    await handleLoadData()
  }, [reloadKey])

  async function handleLoadData() {
    if (dataLoadingRef.current) {
      return
    }
    const key = Math.random()
    dataLoadingRef.current = key

    const data = await loadData()

    // this check will trigger if the reloadKey changes while there is loadData request active
    // theoretically a call to loadData could be triggered after the reloadKey changes which is
    // why we store a random key and check it is the same as opposed to just checking a boolean
    if (dataLoadingRef.current !== key) {
      return
    }

    setData(data)
  }

  const renderContent = useLazyMemo((): React.ReactNode => {
    if (!data) {
      return <LoadingFrame />
    } else {
      return (
        <FloatingBox
          header={
            header ? (
              header
            ) : (
              <React.Fragment>
                <Grid columns={[{ fr: 1 }, "max-content"]} alignItems="center">
                  <div>
                    {resourceName && (
                      <span>
                        <P blockSpacing={0} size="small" weight={600} color="#666">
                          {resourceName}
                        </P>
                      </span>
                    )}
                    <H3
                      size="small"
                      blockSpacing={0}
                      color={viewLink?.(data) ? theme.themeColor("primary") : undefined}
                    >
                      <span
                        css={css`
                          text-decoration: underline;
                        `}
                      >
                        <Target data={data} isTitle />
                      </span>
                    </H3>
                  </div>
                  {viewLink?.(data) && (
                    <Link
                      to={data ? viewLink(data)! : "#"}
                      css={css`
                        text-decoration: none;
                        display: inline-block;
                        margin: -10px;
                      `}
                      target={openInNewTab ? "_blank" : undefined}
                    >
                      <Button buttonStyle={"text"}>View</Button>
                    </Link>
                  )}
                </Grid>
              </React.Fragment>
            )
          }
          css={css`
            min-width: 280px;
            box-shadow: ${theme.boxShadow.xs};
            white-space: initial;
            --floating-box-border-color: ${siteColors.lightBorder};
            --floating-box-header-padding: ${resourceName ? "8px 16px" : "16px"};
            border-bottom: 2px solid ${siteColors.lightBorder};
          `}
        >
          <HoverContent data={data} />
        </FloatingBox>
      )
    }
  }, [data])

  return (
    <div
      css={css`
        line-height: 16px;
        max-width: max-content;
        p {
          line-height: 16px;
        }
      `}
    >
      <div
        css={css`
          width: 100%;
          display: inline-block;
        `}
      >
        <Popover
          getContent={() => renderContent()}
          onShow={() => void handleLoadData()}
          placement={placement}
          offset={offset}
          trigger="click"
          maxWidth={500}
          interactive
          lazy
        >
          <div
            css={css`
              cursor: pointer;
              color: black;
            `}
            className="hover-loader-target"
          >
            {data ? (
              <span
                css={css`
                  text-decoration: underline dotted #7d7d7d;
                  display: inline-block;
                  display: flex;
                  gap: 4px;
                  align-items: center;
                `}
                onDoubleClick={() => {
                  if (viewLink?.(data)) {
                    if (openInNewTab) {
                      window.open(viewLink(data)!, "_blank")
                    } else {
                      history.push(viewLink(data)!)
                    }
                  }
                }}
              >
                {!noHoverIcon && <Icon icon={icons.hoverLoader} color="#333" size={8} />}
                <div
                  css={css`
                    max-width: 100%;
                    white-space: nowrap;
                  `}
                >
                  <Target data={data} />
                  {inlineViewLink && viewLink?.(data) ? (
                    <Link
                      css={css`
                        cursor: pointer;
                        display: block;
                        font-size: 12px;
                      `}
                      to={viewLink(data)!}
                      target={openInNewTab ? "_blank" : undefined}
                    >
                      View
                    </Link>
                  ) : null}
                </div>
              </span>
            ) : (
              <Placeholder
                width="100px"
                height={14}
                css={css`
                  margin: -1px 0 -4px !important;
                `}
              />
            )}
          </div>
        </Popover>
      </div>
    </div>
  )
}

interface IProps {
  header?: React.ReactNode
  children?: React.ReactNode
  className?: string
}

FloatingBox.vars = {
  childPadding: "--floating-box-child-padding",
  headerPadding: "--floating-box-header-padding",
  borderColor: "--floating-box-border-color",
  borderRadius: "--floating-box-border-radius",
}
export function FloatingBox({ className, children, header }: IProps) {
  return (
    <div
      css={css`
        --floating-box-child-padding: 16px;
        --floating-box-header-padding: 16px;
        --floating-box-footer-padding: 16px;
        ${FloatingBox.vars.borderRadius}: 3px;
        --floating-box-border-color: ${siteColors.lightBorder};
        --floating-box-footer-border-width: 1px;

        border: 1px solid var(--floating-box-border-color);
        border-bottom: 3px solid var(--floating-box-border-color);
        border-radius: var(--floating-box-border-radius);
        background: white;
        display: flex;
        flex-direction: column;
      `}
      className={className}
    >
      {header && (
        <div
          css={css`
            padding: var(--floating-box-header-padding);
            border-bottom: 1px solid var(--floating-box-border-color);
          `}
        >
          {header}
        </div>
      )}
      {children && (
        <div
          className="FloatingBox-child"
          css={css`
            padding: var(--floating-box-child-padding);
            flex: 1;
          `}
        >
          {children}
        </div>
      )}
    </div>
  )
}

function Separator() {
  return (
    <div
      css={css`
        height: 1px;
        width: 100% + 32px;
        margin: 0 calc(-1 * var(--floating-box-child-padding));
        background: var(--floating-box-border-color);
      `}
    />
  )
}

FloatingBox.Separator = Separator
